// ** React imports
import React, { Dispatch, SetStateAction, useEffect, useState } from 'react'

// ** MUI imports
import { Chip, FormControl, Input, makeStyles } from '@material-ui/core'
import { Autocomplete, AutocompleteRenderGetTagProps, TextField, Typography } from '@mui/material'
import { X } from 'react-feather'

const styledMultyInput = makeStyles(theme => ({
  formControlRoot: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    width: '100%',
    flexWrap: 'wrap',
    flexDirection: 'row',
    border: '1px solid lightgray',
    boxShadow: '0.5px 0.5px 2px 0px #f0f0f0',
    '&:hover, &:focus, &.ps--clicking': {
      boxShadow: '1px 0.5px 2px 0px #f0f0f0'
    },
    borderRadius: '8px'
  }
}))

interface MultiSelectInputProps {
  id?: string
  label: string
  setValues: Dispatch<SetStateAction<string[]>>
  values: string[]
  options?: any
  placeHolderText?: string
  allowNoSelection?: boolean
  renderOption?: (props: any, option: any) => React.ReactNode
  renderTag?: (option: any) => React.ReactNode
}

const MultiSelectInput: React.FC<MultiSelectInputProps> = ({
  id,
  label,
  setValues,
  values,
  options,
  placeHolderText,
  allowNoSelection = false,
  renderOption,
  renderTag
}) => {
  const classes = styledMultyInput()

  const [currValue, setCurrValue] = useState('')

  const handleKeyUp = (event: React.KeyboardEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    if (event.code == 'Comma' || event.code == 'Space' || event.code == 'Enter') {
      setValues(oldState => [...oldState, currValue])
      setCurrValue('')
    }

    if (event.code == 'Backspace' && Boolean(values?.length)) {
      setValues(values.slice(0, values.length - 1))
      return
    }
  }

  const handleDelete = (roleId: string) => {
    const modifiedValues = [...values]
    const indexToRemove = modifiedValues.findIndex(value => value === roleId)
    modifiedValues?.splice(indexToRemove, 1)
    setValues(modifiedValues)
  }

  return (
    <div className=''>
      <Typography
        variant='body2'
        sx={{
          fontWeight: 500,
          fontSize: 12,
          whiteSpace: 'nowrap'
        }}
      >
        {label}
      </Typography>
      <FormControl classes={{ root: classes.formControlRoot }}>
        <Autocomplete
          id={id}
          multiple
          size='small'
          limitTags={4}
          options={options}
          value={options?.filter((tagOption: any) => values.some(value => tagOption?.value === value))}
          onChange={(event, newValue) => {
            const selectedValues: any = []
            newValue?.forEach((value: any) => {
              selectedValues.push(value.value)
            })
            setValues(selectedValues)
          }}
          renderTags={(tagValue, getTagProps) => (
            <div className='flex flex-wrap gap-1'>
              {tagValue?.map((option: any, index: number) => (
                <Chip
                  key={`${option?.value}_${index}`}
                  style={{ borderRadius: '4px', border: '1px solid #F0F0F0', background: '#FAFAFA' }}
                  size='medium'
                  onDelete={() => handleDelete(option?.value)}
                  label={typeof renderTag !== 'undefined' ? renderTag(option) : option?.label}
                  deleteIcon={<X className={`${allowNoSelection ? '' : index == 0 ? 'hidden' : ''}`} />}
                />
              ))}
            </div>
          )}
          renderOption={
            renderOption
              ? renderOption
              : (props: any, option: any) => {
                  return <li {...props}>{option?.label}</li>
                }
          }
          renderInput={params => <TextField {...params} placeholder={placeHolderText} />}
          sx={{ width: '100%' }}
        />
      </FormControl>
    </div>
  )
}

export default React.memo(MultiSelectInput)
